/**
 * Sistema centralizado de tratamento de erros
 */

export class AppError extends Error {
  code: string;
  statusCode?: number;
  originalError?: any;

  constructor(
    message: string,
    code: string,
    statusCode?: number,
    originalError?: any
  ) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.statusCode = statusCode;
    this.originalError = originalError;
  }
}

export const ErrorCodes = {
  AUTH_ERROR: 'AUTH_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  PERMISSION_ERROR: 'PERMISSION_ERROR',
  NOT_FOUND_ERROR: 'NOT_FOUND_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
} as const;

export type ErrorCode = typeof ErrorCodes[keyof typeof ErrorCodes];

export const ErrorMessages = {
  [ErrorCodes.AUTH_ERROR]: {
    title: 'Erro de Autenticação',
    message: 'Credenciais inválidas. Verifique seus dados de acesso.'
  },
  [ErrorCodes.NETWORK_ERROR]: {
    title: 'Erro de Conexão',
    message: 'Não foi possível conectar ao servidor. Verifique sua conexão.'
  },
  [ErrorCodes.VALIDATION_ERROR]: {
    title: 'Dados Inválidos',
    message: 'Verifique os dados informados e tente novamente.'
  },
  [ErrorCodes.PERMISSION_ERROR]: {
    title: 'Acesso Negado',
    message: 'Você não tem permissão para realizar esta ação.'
  },
  [ErrorCodes.NOT_FOUND_ERROR]: {
    title: 'Não Encontrado',
    message: 'O recurso solicitado não foi encontrado.'
  },
  [ErrorCodes.SERVER_ERROR]: {
    title: 'Erro do Servidor',
    message: 'Erro interno do servidor. Tente novamente em alguns minutos.'
  },
  [ErrorCodes.TIMEOUT_ERROR]: {
    title: 'Tempo Esgotado',
    message: 'A operação demorou mais que o esperado. Tente novamente.'
  },
  [ErrorCodes.UNKNOWN_ERROR]: {
    title: 'Erro Desconhecido',
    message: 'Ocorreu um erro inesperado. Tente novamente.'
  }
};

/**
 * Transforma erros de diferentes fontes em AppError padronizado
 */
export const handleApiError = (error: any): AppError => {
  // Erro de rede/conexão
  if (error.code === 'ERR_NETWORK' || !error.response) {
    return new AppError(
      ErrorMessages[ErrorCodes.NETWORK_ERROR].message,
      ErrorCodes.NETWORK_ERROR,
      undefined,
      error
    );
  }

  const status = error.response?.status;
  const data = error.response?.data;

  switch (status) {
    case 401:
      // Para endpoints de login, usar mensagem específica
      if (error.config?.url?.includes('/auth/login')) {
        return new AppError(
          data?.message || 'Login ou senha incorretos. Verifique suas credenciais.',
          ErrorCodes.AUTH_ERROR,
          401,
          error
        );
      }
      return new AppError(
        data?.message || ErrorMessages[ErrorCodes.AUTH_ERROR].message,
        ErrorCodes.AUTH_ERROR,
        401,
        error
      );
      
    case 403:
      return new AppError(
        data?.message || ErrorMessages[ErrorCodes.PERMISSION_ERROR].message,
        ErrorCodes.PERMISSION_ERROR,
        403,
        error
      );
      
    case 404:
      return new AppError(
        data?.message || ErrorMessages[ErrorCodes.NOT_FOUND_ERROR].message,
        ErrorCodes.NOT_FOUND_ERROR,
        404,
        error
      );
      
    case 400:
      // Para endpoints de login, tratar 400 como erro de credenciais
      if (error.config?.url?.includes('/auth/login')) {
        return new AppError(
          data?.message || 'Credenciais inválidas. Verifique seu login e senha.',
          ErrorCodes.AUTH_ERROR,
          400,
          error
        );
      }
      return new AppError(
        data?.message || ErrorMessages[ErrorCodes.VALIDATION_ERROR].message,
        ErrorCodes.VALIDATION_ERROR,
        400,
        error
      );
      
    case 422:
      return new AppError(
        data?.message || ErrorMessages[ErrorCodes.VALIDATION_ERROR].message,
        ErrorCodes.VALIDATION_ERROR,
        422,
        error
      );
      
    case 500:
    case 502:
    case 503:
    case 504:
      return new AppError(
        data?.message || ErrorMessages[ErrorCodes.SERVER_ERROR].message,
        ErrorCodes.SERVER_ERROR,
        status,
        error
      );
      
    default:
      return new AppError(
        data?.message || ErrorMessages[ErrorCodes.UNKNOWN_ERROR].message,
        ErrorCodes.UNKNOWN_ERROR,
        status,
        error
      );
  }
};

/**
 * Logger centralizado para erros
 */
export const logError = (error: AppError, context?: Record<string, any>) => {
  // Criar uma cópia segura do contexto, removendo possíveis referências circulares
  const safeContext = context ? JSON.parse(JSON.stringify(context, (_key, value) => {
    // Substituir funções e objetos complexos por strings descritivas
    if (typeof value === 'function') return '[Function]';
    if (typeof value === 'object' && value !== null) {
      // Se for um objeto com muitas propriedades, simplificar
      if (Object.keys(value).length > 10) {
        return '[Complex Object]';
      }
    }
    return value;
  })) : undefined;

  const logData = {
    message: error.message,
    code: error.code,
    statusCode: error.statusCode,
    context: safeContext,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  // Em desenvolvimento, log no console de forma segura
  if (import.meta.env.DEV) {
    console.group(`[AppError] ${error.code}`);
    console.error('Message:', error.message);
    console.error('Status Code:', error.statusCode);
    console.error('Context:', safeContext);
    console.error('Timestamp:', logData.timestamp);
    
    // Log do erro original apenas se não for circular
    if (error.originalError) {
      try {
        JSON.stringify(error.originalError);
        console.error('Original Error:', error.originalError);
      } catch (e) {
        console.error('Original Error:', '[Circular Reference]', error.originalError?.message || 'Unknown');
      }
    }
    console.groupEnd();
  }

  // Em produção, enviar para serviço de monitoramento
  // TODO: Integrar com Sentry, LogRocket, etc.
  
  return logData;
};